import { GeneratedImage, GenerateImageOptions, GenerateImageRendererOptions, generateImages, getVariantsFromModel, IModelFileType, ModelData, ModelFileType } from "@centricsoftware/model-viewer";
import { uploadSnapshots } from "csi/state/3D/snapshotsService";
import { GenerateImageFormat, MaterialSide, MaterialSide as MaterialSideEnum, SnapshotMimeType } from "csi/threeD/snapshotTemplates/snapshotUtils";
import { ATTR_PARENT, C8Node, C8NodeType, cnl } from "csi/typings";
import { isDefined } from "csi/utility/coreUtility";
import { getFileNameWithoutExtension } from "csi/utility/fileUtility";
import { pathCreate } from "csi/utility/pathUtility";

export interface ImageDataMap {
	file: File;
	isTurntable: boolean;	
	turntableFiles?: File[];
}

export interface ImageConfig {
	docNodeName: string;
	fileType: IModelFileType;
	fileName: ModelFileType ;
	modelData: ModelData;
	revisionNode: C8Node;
}

export interface TemplateConfig {
	imageOptions: GenerateImageOptions;
	includeAllVariants: boolean;
	rendererOptions: GenerateImageRendererOptions;
	templateName: string;
}

const DOCUMENT_OWNER_PATH = pathCreate("DocumentsAndComments", "Documents");

const PATHS_TO_DOCUMENT_CONTAINER: Record<C8NodeType, string> = {
	Colorway:ATTR_PARENT,
	Material: "",
	Style: "",
};

/** Determine if the node type can generate images */
export function isImageGenerateType(type: C8NodeType): boolean {
	return isDefined(PATHS_TO_DOCUMENT_CONTAINER[type]);
}

/** Determine the path to get the documents for a node type */
export function getDocumentPath(type: C8NodeType): string {
	return pathCreate(PATHS_TO_DOCUMENT_CONTAINER[type], DOCUMENT_OWNER_PATH);
}

/** Utility function to deep clone and modify image options */
export function prepareImageOptions(
	imageOptions: GenerateImageOptions,
	includeAllVariants: boolean,
	gltfData: any,
): GenerateImageOptions {
	const imageOptionsCopy = JSON.parse(JSON.stringify(imageOptions));
  
	if (includeAllVariants) {
	  const variants = getVariantsFromModel(gltfData);
	  imageOptionsCopy.materials.variants = variants;
	}
    
	return imageOptionsCopy;
}

/** Convert the image data returned from the Model Viewer Generate Images function into a ImageData */
export function convertImagesToImageData(nestedImages: GeneratedImage[][], fileName: ModelFileType, mimeType: GenerateImageFormat, isTurntable: boolean): ImageDataMap[] {
	const data: ImageDataMap[] = [];
	const format = mimeType.toLowerCase();
	const name = typeof fileName === "string" ? fileName : "GeneratedImage";
	nestedImages.forEach((images) => {
		if (isTurntable){
			const turntableFiles = images.map((image, index) => {
				const snapshotName = `${index + 1}.${format}`;
				return new File([image.blob], snapshotName, { type: image.blob.type });
			});
			const turntableData = {
				file: turntableFiles[0],
				isTurntable: true,
				turntableFiles,
			};
			data.push(turntableData);
		} else {
			images.forEach((image, index) => {
				const snapshotName = `${getFileNameWithoutExtension(name)}_${Date.now()}_${index}.${format}`;
				const file = new File([image.blob], snapshotName, { type: image.blob.type });
				const imageData = {
					file,
					isTurntable: false,
				};
				data.push(imageData);
			});
		}
	});
	return data;
}

/** Generate Images */
export async function generateAndUploadImagesToNode(documentConfigurations: ImageConfig[], templateConfigurations: TemplateConfig[], nodeUrl: cnl, signal: AbortSignal): Promise<void> {		
	const images: ImageDataMap[] = [];	
	const checkAbort = (): void => {
		if (signal.aborted) {
			throw new Error("Operation aborted");
		}
	};
	
	const imageGenerationPromises = templateConfigurations.map(async (templateConfig) => {
		checkAbort();
		const { imageOptions, rendererOptions, includeAllVariants } = templateConfig;
		const imageFormat = rendererOptions.imageType === SnapshotMimeType.JPEG ? GenerateImageFormat.JPEG : GenerateImageFormat.PNG;
		const isTurntable = imageOptions?.camera?.turntable != null;
		
		const documentPromises = documentConfigurations.map(async (documentConfig) => {
			checkAbort();
			const { modelData: { gltfData, modelData }, fileName } = documentConfig;
			const deepCopyOfImageOptions = prepareImageOptions(
				imageOptions,
				includeAllVariants,
				gltfData,
			  );
			const allUniqueImages = await generateImages(modelData, deepCopyOfImageOptions, rendererOptions, gltfData, signal);
			const imageData = convertImagesToImageData(allUniqueImages, fileName, imageFormat, isTurntable);

			images.push(...imageData);
		});				
		await Promise.all(documentPromises);
	});
	checkAbort();
	await Promise.all(imageGenerationPromises);
	await uploadSnapshots(images, [nodeUrl]);
};